import type { ComponentType } from 'react';
import { memo } from 'react';

import type { Theme } from '@emotion/react';
import { jsx } from '@emotion/react';
import styled from '@emotion/styled';

import { hasString } from '@twisto/utils';

import * as styles from './icons.styles';
import type { IconA11yProps, SvgProps } from './types';

export type IconProps = {
  color?: keyof typeof styles.colors;
  size?: keyof typeof styles.sizes;
} & SvgProps;

export function createSvgIconFromGenerated(SvgNode: ComponentType, displayName: string) {
  const StyledSvg = styled(SvgNode)<IconProps>(
    styles.root,
    (props: { theme: Theme; color?: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const colorKey = (props.color as keyof typeof styles.colors) || 'inherit';

      return styles.colors[colorKey](props.theme);
    },
    (props: { size?: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const sizeKey = (props.size as keyof typeof styles.sizes) || '30';

      return styles.sizes[sizeKey];
    },
    (props) => props.onClick && styles.clickable,
  );

  const Icon = memo((props: IconProps) => {
    const { titleAccess, ...other } = props;
    const a11yProps = createIconA11yProps({ titleAccess });

    return jsx(StyledSvg, {
      ...a11yProps,
      ...other,
    });
  });
  Icon.displayName = `${displayName}Icon`;

  return Icon;
}

export function createIconA11yProps({ titleAccess }: IconA11yProps) {
  return {
    title: titleAccess,
    role: hasString(titleAccess) ? 'img' : 'presentation',
    'aria-hidden': hasString(titleAccess) ? 'false' : 'true',
  } as const;
}
